import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Play, Pause, Square, ArrowLeft, Check, X as XIcon, Loader,
  ExternalLink, Plus, GripVertical, ChevronDown, ChevronRight, Edit2,
  Send, Settings as SettingsIcon, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { cmsFetch } from '../lib/cmsApi';
import CMSAuth from '../components/CMSAuth';

const POLL_MS = 5000;

// ──────────────────────────────────────────────────────────────────────────
// Small UI helpers
// ──────────────────────────────────────────────────────────────────────────

const statusBadgeClass = (status) => ({
  draft:      'bg-gray-100 text-gray-700',
  generating: 'bg-blue-100 text-blue-800',
  paused:     'bg-amber-100 text-amber-800',
  complete:   'bg-green-100 text-green-800',
  stopped:    'bg-red-100 text-red-700',
}[status] || 'bg-gray-100 text-gray-600');

const walkStatusIcon = (status) => {
  if (status === 'pending')    return <span className="text-gray-400">⏳</span>;
  if (status === 'generating') return <Loader className="w-3.5 h-3.5 text-blue-600 animate-spin inline-block" />;
  if (status === 'generated')  return <Check className="w-3.5 h-3.5 text-green-600 inline-block" />;
  if (status === 'published')  return <Send className="w-3.5 h-3.5 text-blue-700 inline-block" />;
  if (status === 'skipped')    return <RefreshCw className="w-3.5 h-3.5 text-amber-600 inline-block" />;
  if (status === 'failed')     return <XIcon className="w-3.5 h-3.5 text-red-600 inline-block" />;
  return null;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return '—'; }
};

// ──────────────────────────────────────────────────────────────────────────
// List View
// ──────────────────────────────────────────────────────────────────────────

const CampaignListView = ({ navigate, flashStatus }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const seededRef = useRef(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      const resp = await cmsFetch('/api/cms/campaigns');
      if (resp.ok) {
        const data = await resp.json();
        setCampaigns(data.campaigns || []);
        return data.campaigns || [];
      }
    } catch (e) { /* ignore */ }
    return [];
  }, []);

  // Initial load + auto-seed LA Core when collection is empty (first-load behavior)
  useEffect(() => {
    (async () => {
      const list = await fetchCampaigns();
      setLoading(false);
      if (list.length === 0 && !seededRef.current) {
        seededRef.current = true;
        try {
          const resp = await cmsFetch('/api/cms/campaigns/from-template/la-core-200', {
            method: 'POST',
            body: JSON.stringify({ name: 'LA Core — 172 Walks' }),
          });
          if (resp.ok) {
            const camp = await resp.json();
            flashStatus('success', 'Created your first campaign: LA Core — 172 Walks');
            navigate(`/cms/bulk-generate/${camp.id}`);
          }
        } catch { /* ignore */ }
      }
    })();
  }, [fetchCampaigns, navigate, flashStatus]);

  const stats = useMemo(() => {
    const total = campaigns.length;
    const generated = campaigns.reduce((n, c) => n + (c.stats?.generated || 0), 0);
    const published = campaigns.reduce((n, c) => n + (c.stats?.published || 0), 0);
    const pending = generated - published;
    return { total, generated, published, pending };
  }, [campaigns]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/cms/dashboard" className="text-gray-400 hover:text-gray-600"><ArrowLeft className="w-5 h-5" /></Link>
          <h1 className="text-2xl font-bold text-gray-900">Walk Pipeline</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowTemplates(true)} variant="outline">From Template</Button>
          <Button onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatTile label="Campaigns" value={stats.total} />
        <StatTile label="Walks generated" value={stats.generated} />
        <StatTile label="Walks published" value={stats.published} />
        <StatTile label="Pending publication" value={Math.max(0, stats.pending)} />
      </div>

      {loading ? (
        <div className="bg-white rounded-lg border p-12 text-center text-gray-400 text-sm">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <p className="text-gray-600 mb-2">No campaigns yet.</p>
          <p className="text-sm text-gray-400">Create one from a template or build your own.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {campaigns.map(c => <CampaignCard key={c.id} campaign={c} navigate={navigate} onChange={fetchCampaigns} flashStatus={flashStatus} />)}
        </div>
      )}

      {showTemplates && <TemplatesModal onClose={() => setShowTemplates(false)} onCreated={async (camp) => { setShowTemplates(false); await fetchCampaigns(); navigate(`/cms/bulk-generate/${camp.id}`); }} flashStatus={flashStatus} />}
      {showNew && <NewCampaignModal onClose={() => setShowNew(false)} onCreated={async (camp) => { setShowNew(false); await fetchCampaigns(); navigate(`/cms/bulk-generate/${camp.id}`); }} flashStatus={flashStatus} />}
    </div>
  );
};

const StatTile = ({ label, value }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 uppercase tracking-wide mt-0.5">{label}</p>
  </div>
);

const CampaignCard = ({ campaign, navigate, onChange, flashStatus }) => {
  const stats = campaign.stats || {};
  const totalWalks = stats.total || (campaign.walks?.length || 0);
  const generated = stats.generated || 0;
  const published = stats.published || 0;
  const genPct = totalWalks ? Math.round((generated / totalWalks) * 100) : 0;
  const pubPct = generated ? Math.round((published / generated) * 100) : 0;

  const onStart = async (e) => {
    e.stopPropagation();
    const resp = await cmsFetch(`/api/cms/campaigns/${campaign.id}/start`, { method: 'POST' });
    if (resp.ok) flashStatus('success', 'Campaign started.');
    else flashStatus('error', `Start failed: ${resp.status}`);
    onChange();
  };
  const onPauseResume = async (e) => {
    e.stopPropagation();
    const path = campaign.status === 'paused' ? 'resume' : 'pause';
    await cmsFetch(`/api/cms/campaigns/${campaign.id}/${path}`, { method: 'POST' });
    onChange();
  };
  const onDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete draft campaign "${campaign.name}"?`)) return;
    const resp = await cmsFetch(`/api/cms/campaigns/${campaign.id}`, { method: 'DELETE' });
    if (resp.ok) { flashStatus('success', 'Campaign deleted.'); onChange(); }
    else flashStatus('error', `Delete failed: ${resp.status}`);
  };

  return (
    <div onClick={() => navigate(`/cms/bulk-generate/${campaign.id}`)} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{campaign.region_label}</p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadgeClass(campaign.status)}`}>{campaign.status}</span>
      </div>

      <div className="space-y-2 mb-3">
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Generated</span><span>{generated} / {totalWalks}</span></div>
          <div className="w-full bg-gray-200 rounded-full h-1.5"><div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${genPct}%` }} /></div>
        </div>
        {generated > 0 && (
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Published</span><span>{published} / {generated}</span></div>
            <div className="w-full bg-gray-200 rounded-full h-1.5"><div className="bg-green-600 h-1.5 rounded-full" style={{ width: `${pubPct}%` }} /></div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>Created {fmtDate(campaign.created_at)}</span>
        <div className="flex items-center gap-1">
          {campaign.status === 'draft' && <button onClick={onStart} className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded">Start</button>}
          {['generating', 'paused'].includes(campaign.status) && <button onClick={onPauseResume} className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded">{campaign.status === 'paused' ? 'Resume' : 'Pause'}</button>}
          <button onClick={(e) => { e.stopPropagation(); navigate(`/cms/bulk-generate/${campaign.id}`); }} className="px-2 py-1 text-gray-600 hover:bg-gray-50 rounded">View Queue</button>
          {campaign.status === 'draft' && <button onClick={onDelete} className="px-2 py-1 text-red-600 hover:bg-red-50 rounded">Delete</button>}
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Templates / New campaign modals
// ──────────────────────────────────────────────────────────────────────────

const ModalShell = ({ title, onClose, children, maxWidth = 'max-w-3xl' }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
    <div className={`bg-white rounded-xl shadow-2xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
      <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XIcon className="w-5 h-5" /></button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const TemplatesModal = ({ onClose, onCreated, flashStatus }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const resp = await cmsFetch('/api/cms/campaigns/templates');
        if (resp.ok) {
          const data = await resp.json();
          setTemplates(data.templates || []);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const useTemplate = async (tid) => {
    setCreating(tid);
    try {
      const resp = await cmsFetch(`/api/cms/campaigns/from-template/${tid}`, { method: 'POST', body: JSON.stringify({}) });
      if (!resp.ok) { flashStatus('error', `Create failed: ${resp.status}`); return; }
      const camp = await resp.json();
      onCreated(camp);
    } finally { setCreating(null); }
  };

  return (
    <ModalShell title="Create from Template" onClose={onClose}>
      {loading ? <p className="text-sm text-gray-400">Loading templates…</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map(t => (
            <button key={t.id} onClick={() => useTemplate(t.id)} disabled={creating === t.id}
              className="text-left border border-gray-200 hover:border-blue-400 hover:shadow-sm rounded-lg p-4 disabled:opacity-50">
              <h3 className="font-semibold text-gray-900">{t.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t.region_label}</p>
              {t.description && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{t.description}</p>}
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-gray-400">{t.walk_count} walks</span>
                {creating === t.id ? <Loader className="w-3 h-3 animate-spin text-blue-600" /> : <span className="text-blue-600 font-medium">Use →</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </ModalShell>
  );
};

const NewCampaignModal = ({ onClose, onCreated, flashStatus }) => {
  const [name, setName] = useState('');
  const [regionLabel, setRegionLabel] = useState('');
  const [city, setCity] = useState('los-angeles');
  const [country, setCountry] = useState('us');
  const [walksText, setWalksText] = useState('');
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (!name.trim() || !regionLabel.trim()) { flashStatus('error', 'Name and region are required.'); return; }
    const walks = walksText.split('\n').map(s => s.trim()).filter(Boolean).map(line => ({ concept: line, area: '', neighborhood: '' }));
    setCreating(true);
    try {
      const resp = await cmsFetch('/api/cms/campaigns', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), region_label: regionLabel.trim(), city: city.trim(), country: country.trim(), walks: walks.length ? walks : undefined }),
      });
      if (!resp.ok) { flashStatus('error', `Create failed: ${resp.status}`); return; }
      const camp = await resp.json();
      onCreated(camp);
    } finally { setCreating(false); }
  };

  return (
    <ModalShell title="New Campaign" onClose={onClose} maxWidth="max-w-xl">
      <div className="space-y-3 text-sm">
        <div>
          <label className="block font-medium text-gray-700 mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border-gray-300" placeholder="e.g. SF Bay Area 50" />
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Region label</label>
          <input value={regionLabel} onChange={(e) => setRegionLabel(e.target.value)} className="w-full rounded-md border-gray-300" placeholder="e.g. San Francisco, CA" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-medium text-gray-700 mb-1">City slug</label>
            <input value={city} onChange={(e) => setCity(e.target.value)} className="w-full rounded-md border-gray-300 font-mono text-xs" />
          </div>
          <div>
            <label className="block font-medium text-gray-700 mb-1">Country</label>
            <input value={country} onChange={(e) => setCountry(e.target.value)} className="w-full rounded-md border-gray-300 font-mono text-xs" />
          </div>
        </div>
        <div>
          <label className="block font-medium text-gray-700 mb-1">Walk concepts (one per line — optional)</label>
          <textarea value={walksText} onChange={(e) => setWalksText(e.target.value)} rows={6} className="w-full rounded-md border-gray-300 text-xs font-mono" placeholder={`Mission District: Murals, Murals & More Murals\nGolden Gate Park: From Conservatory to Ocean Beach\n…`} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
            {creating ? <><Loader className="w-4 h-4 animate-spin mr-2" />Creating…</> : 'Create Campaign'}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Detail View
// ──────────────────────────────────────────────────────────────────────────

const CampaignDetailView = ({ campaignId, navigate, flashStatus }) => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [dragId, setDragId] = useState(null);

  // Settings local state
  const [delaySeconds, setDelaySeconds] = useState(45);
  const [publishMode, setPublishMode] = useState('immediate'); // immediate | daily
  const [publishRate, setPublishRate] = useState(25);
  const [publishStart, setPublishStart] = useState('');

  const pollTimer = useRef(null);

  const fetchCampaign = useCallback(async () => {
    try {
      const resp = await cmsFetch(`/api/cms/campaigns/${campaignId}`);
      if (resp.ok) {
        const camp = await resp.json();
        setCampaign(camp);
        // hydrate settings on first load
        if (loading) {
          const s = camp.settings || {};
          setDelaySeconds(s.delay_seconds || 45);
          if ((s.publish_rate_per_day || 0) > 0) {
            setPublishMode('daily');
            setPublishRate(s.publish_rate_per_day);
            setPublishStart(s.publish_start_date ? new Date(s.publish_start_date).toISOString().slice(0, 10) : '');
          }
          setNameDraft(camp.name || '');
        }
        return camp;
      }
      if (resp.status === 404) {
        flashStatus('error', 'Campaign not found.');
        navigate('/cms/bulk-generate');
      }
    } finally { setLoading(false); }
  }, [campaignId, loading, navigate, flashStatus]);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);

  // Poll every 5s while generating/paused
  useEffect(() => {
    clearInterval(pollTimer.current);
    if (!campaign) return;
    if (!['generating', 'paused'].includes(campaign.status)) return;
    pollTimer.current = setInterval(fetchCampaign, POLL_MS);
    return () => clearInterval(pollTimer.current);
  }, [campaign?.status, fetchCampaign, campaign]);

  if (loading) return <div className="bg-white rounded-lg border p-12 text-center text-gray-400 text-sm">Loading…</div>;
  if (!campaign) return null;

  const walks = campaign.walks || [];
  const stats = campaign.stats || {};
  const total = stats.total || walks.length;
  const generated = stats.generated || 0;
  const published = stats.published || 0;
  const skipped = stats.skipped || 0;
  const failed = stats.failed || 0;
  const isRunning = ['generating', 'paused'].includes(campaign.status);
  const genPct = total ? Math.round((generated / total) * 100) : 0;
  const settings = campaign.settings || {};
  const cadenceActive = (settings.publish_rate_per_day || 0) > 0;

  const remaining = Math.max(0, total - generated);
  const etaSec = remaining * (settings.delay_seconds || 45);
  const etaMin = Math.floor(etaSec / 60);
  const etaHrs = Math.floor(etaMin / 60);
  const eta = etaHrs > 0 ? `~${etaHrs}h ${etaMin % 60}m` : `~${etaMin}m`;

  const filtered = walks.filter(w => filter === 'all' || w.status === filter);

  // ── Settings save
  const saveSettings = async () => {
    const payload = {
      settings: {
        delay_seconds: delaySeconds,
        publish_rate_per_day: publishMode === 'daily' ? publishRate : 0,
        publish_start_date: publishMode === 'daily' && publishStart ? new Date(publishStart).toISOString() : null,
      },
    };
    const resp = await cmsFetch(`/api/cms/campaigns/${campaignId}`, { method: 'PUT', body: JSON.stringify(payload) });
    if (resp.ok) flashStatus('success', 'Settings saved.'); else flashStatus('error', `Save failed: ${resp.status}`);
    fetchCampaign();
  };

  const saveName = async () => {
    if (!nameDraft.trim() || nameDraft === campaign.name) { setEditingName(false); return; }
    await cmsFetch(`/api/cms/campaigns/${campaignId}`, { method: 'PUT', body: JSON.stringify({ name: nameDraft.trim() }) });
    setEditingName(false);
    fetchCampaign();
  };

  // ── Control
  const ctl = async (action) => {
    const resp = await cmsFetch(`/api/cms/campaigns/${campaignId}/${action}`, { method: 'POST' });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      flashStatus('error', body?.detail?.message || `${action} failed: ${resp.status}`);
    } else {
      flashStatus('success', `Campaign ${action}.`);
    }
    fetchCampaign();
  };

  const publishNext = async () => {
    const resp = await cmsFetch(`/api/cms/campaigns/${campaignId}/publish-next`, {
      method: 'POST',
      body: JSON.stringify({ count: publishRate || 25 }),
    });
    if (resp.ok) {
      const data = await resp.json();
      flashStatus('success', `Published ${data.published || 0} walks.`);
    } else {
      flashStatus('error', `Publish failed: ${resp.status}`);
    }
    fetchCampaign();
  };

  // ── Walk row actions
  const updateWalkConcept = async (walkId, newConcept) => {
    await cmsFetch(`/api/cms/campaigns/${campaignId}/walks/${walkId}`, { method: 'PUT', body: JSON.stringify({ concept: newConcept }) });
    fetchCampaign();
  };
  const skipWalk = async (walkId) => {
    await cmsFetch(`/api/cms/campaigns/${campaignId}/walks/${walkId}`, { method: 'PUT', body: JSON.stringify({ status: 'skip' }) });
    fetchCampaign();
  };

  // ── Drag reorder (pending walks only). HTML5 native.
  const onDragStart = (walk) => (e) => {
    if (walk.status !== 'pending') return;
    setDragId(walk.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (targetWalk) => async (e) => {
    e.preventDefault();
    if (!dragId || targetWalk.status !== 'pending' || targetWalk.id === dragId) { setDragId(null); return; }
    // Build a new pending order locally, then send to server
    const pending = walks.filter(w => w.status === 'pending');
    const fromIdx = pending.findIndex(w => w.id === dragId);
    const toIdx = pending.findIndex(w => w.id === targetWalk.id);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); return; }
    const reordered = pending.slice();
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const items = reordered.map((w, i) => ({ walk_id: w.id, position: i }));
    setDragId(null);
    await cmsFetch(`/api/cms/campaigns/${campaignId}/walks/reorder`, { method: 'POST', body: JSON.stringify({ items }) });
    fetchCampaign();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link to="/cms/bulk-generate" className="text-gray-400 hover:text-gray-600"><ArrowLeft className="w-5 h-5" /></Link>
          {editingName ? (
            <input
              autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveName} onKeyDown={(e) => e.key === 'Enter' && saveName()}
              className="text-2xl font-bold border-b border-gray-300 px-1 focus:outline-none focus:border-blue-500 bg-transparent"
            />
          ) : (
            <h1 className="text-2xl font-bold text-gray-900 cursor-pointer hover:bg-gray-100 px-1 rounded" onClick={() => setEditingName(true)}>
              {campaign.name}
            </h1>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadgeClass(campaign.status)}`}>{campaign.status}</span>
        </div>
        <span className="text-sm text-gray-500">{campaign.region_label}</span>
      </div>

      {/* Settings panel */}
      <div className="bg-white rounded-lg border border-gray-200 mb-4">
        <button onClick={() => setSettingsOpen(!settingsOpen)} className="w-full px-5 py-3 flex items-center justify-between text-left">
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-gray-900 text-sm">Settings</span>
            <span className="text-xs text-gray-500">
              · {settings.delay_seconds || 45}s delay
              · {cadenceActive ? `Publishing ${settings.publish_rate_per_day}/day` : 'Publish immediately'}
            </span>
          </div>
          {settingsOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        {settingsOpen && (
          <div className="border-t px-5 py-4 space-y-4 text-sm">
            <div>
              <label className="block font-medium text-gray-700 mb-1">Delay between walks: <span className="font-mono">{delaySeconds}s</span></label>
              <input type="range" min={30} max={120} step={5} value={delaySeconds} onChange={(e) => setDelaySeconds(parseInt(e.target.value, 10))} className="w-full" />
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-2">Publishing mode</p>
              <label className="flex items-center gap-2 mb-2">
                <input type="radio" name="publishMode" checked={publishMode === 'immediate'} onChange={() => setPublishMode('immediate')} />
                <span>Publish immediately as generated</span>
              </label>
              <label className="flex items-center gap-2 mb-2">
                <input type="radio" name="publishMode" checked={publishMode === 'daily'} onChange={() => setPublishMode('daily')} />
                <span>Publish</span>
                <input type="number" min={1} max={50} value={publishRate} onChange={(e) => setPublishRate(parseInt(e.target.value, 10) || 1)} disabled={publishMode !== 'daily'} className="w-20 rounded-md border-gray-300 text-sm" />
                <span>walks per day, starting</span>
                <input type="date" value={publishStart} onChange={(e) => setPublishStart(e.target.value)} disabled={publishMode !== 'daily'} className="rounded-md border-gray-300 text-sm" />
              </label>
            </div>
            <Button onClick={saveSettings} className="bg-blue-600 hover:bg-blue-700 text-white">Save Settings</Button>
          </div>
        )}
      </div>

      {/* Generation progress */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-gray-900">{generated} of {total} generated{isRunning && remaining > 0 ? ` · Est. ${eta} remaining` : ''}</h2>
          <div className="flex items-center gap-2">
            {campaign.status === 'draft' && <Button onClick={() => ctl('start')} className="bg-blue-600 hover:bg-blue-700 text-white"><Play className="w-4 h-4 mr-2" />Start Generation</Button>}
            {campaign.status === 'generating' && <Button onClick={() => ctl('pause')} variant="outline"><Pause className="w-4 h-4 mr-2" />Pause</Button>}
            {campaign.status === 'paused' && <Button onClick={() => ctl('resume')} className="bg-blue-600 hover:bg-blue-700 text-white"><Play className="w-4 h-4 mr-2" />Resume</Button>}
            {isRunning && <Button onClick={() => { if (window.confirm('Stop generation? Already-generated walks remain.')) ctl('stop'); }} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"><Square className="w-4 h-4 mr-2" />Stop</Button>}
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden"><div className="bg-blue-600 h-2 transition-all" style={{ width: `${genPct}%` }} /></div>
        <div className="flex justify-between text-xs text-gray-500 mt-1.5">
          <span>{genPct}% · {skipped} skipped · {failed} failed</span>
          <span>{campaign.status === 'generating' && campaign.walks?.find(w => w.status === 'generating')?.concept}</span>
        </div>
      </div>

      {/* Publishing cadence panel */}
      {cadenceActive && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="font-medium text-gray-900">{published} of {total} published</p>
              <p className="text-xs text-gray-500">Publishing {settings.publish_rate_per_day}/day · cadence task runs hourly</p>
            </div>
            <Button onClick={publishNext} variant="outline"><Send className="w-4 h-4 mr-2" />Publish Next {settings.publish_rate_per_day || 25} Now</Button>
          </div>
        </div>
      )}

      {/* Walk queue table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-sm">Queue ({walks.length} walks)</h3>
          <div className="flex gap-1 text-xs">
            {['all', 'pending', 'generated', 'published', 'failed'].map(f => (
              <button key={f} onClick={() => setFilter(f)} className={`px-2 py-1 rounded-full font-medium ${filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="w-8 px-2"></th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10">#</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Neighborhood</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Walk Concept</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Published</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filtered.map(walk => (
                <WalkRow
                  key={walk.id}
                  walk={walk}
                  campaign={campaign}
                  onConceptSave={updateWalkConcept}
                  onSkip={skipWalk}
                  onDragStart={onDragStart(walk)}
                  onDragOver={onDragOver}
                  onDrop={onDrop(walk)}
                  isDragging={dragId === walk.id}
                />
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-gray-400 text-sm">No walks match the filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const WalkRow = ({ walk, campaign, onConceptSave, onSkip, onDragStart, onDragOver, onDrop, isDragging }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(walk.concept);
  const isPending = walk.status === 'pending';

  const save = () => {
    if (draft.trim() && draft !== walk.concept) onConceptSave(walk.id, draft.trim());
    setEditing(false);
  };

  const walkPath = walk.walk_slug
    ? `/walks/${campaign.country || 'us'}/${campaign.city || 'los-angeles'}/${(walk.neighborhood || '').toLowerCase().replace(/\s+/g, '-')}/${walk.walk_slug}`
    : null;

  return (
    <tr
      draggable={isPending}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`${isDragging ? 'opacity-50' : ''} ${isPending ? 'hover:bg-gray-50' : ''}`}
    >
      <td className="px-2 text-gray-300 text-center">{isPending && <GripVertical className="w-3.5 h-3.5 mx-auto cursor-grab" />}</td>
      <td className="px-3 py-1.5 text-xs text-gray-400 font-mono">{(walk.position ?? 0) + 1}</td>
      <td className="px-3 py-1.5 text-xs text-gray-700">{walk.neighborhood}</td>
      <td className="px-3 py-1.5 text-sm text-gray-900">
        {editing ? (
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && save()} className="w-full rounded-md border-gray-300 text-sm" />
        ) : (
          <span className={isPending ? 'cursor-pointer hover:bg-gray-100 px-1 rounded' : ''} onClick={() => isPending && setEditing(true)}>{walk.concept}</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs">
        <span className="inline-flex items-center gap-1">
          {walkStatusIcon(walk.status)}
          <span className="capitalize text-gray-700">{walk.status}</span>
        </span>
        {walk.error && <p className="text-xs text-red-600 mt-0.5">{walk.error}</p>}
      </td>
      <td className="px-3 py-1.5 text-xs text-gray-500">{walk.published_at ? fmtDate(walk.published_at) : (walk.scheduled_publish_date ? `~${fmtDate(walk.scheduled_publish_date)}` : '—')}</td>
      <td className="px-3 py-1.5 text-right text-xs">
        <div className="flex items-center justify-end gap-1">
          {isPending && <button onClick={() => setEditing(true)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Edit concept"><Edit2 className="w-3.5 h-3.5" /></button>}
          {isPending && <button onClick={() => onSkip(walk.id)} className="p-1 text-amber-600 hover:bg-amber-50 rounded" title="Skip"><RefreshCw className="w-3.5 h-3.5" /></button>}
          {walkPath && <a href={walkPath} target="_blank" rel="noopener noreferrer" className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="View live walk"><ExternalLink className="w-3.5 h-3.5" /></a>}
        </div>
      </td>
    </tr>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Page wrapper — gates auth, switches between list / detail by URL param
// ──────────────────────────────────────────────────────────────────────────

const CMSBulkGeneratePage = () => {
  const { isAuthenticated } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const { campaignId } = useParams();

  useEffect(() => { setHasAccess(isAuthenticated); }, [isAuthenticated]);

  const flashStatus = useCallback((kind, message, ms = 4000) => {
    setStatus({ kind, message });
    setTimeout(() => setStatus(null), ms);
  }, []);

  if (!hasAccess) return <CMSAuth onAuthenticated={() => setHasAccess(true)} />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {status && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            status.kind === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            status.kind === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-amber-50 text-amber-800 border border-amber-200'
          }`}>{status.message}</div>
        )}
        {campaignId
          ? <CampaignDetailView campaignId={campaignId} navigate={navigate} flashStatus={flashStatus} />
          : <CampaignListView navigate={navigate} flashStatus={flashStatus} />
        }
      </div>
    </div>
  );
};

export default CMSBulkGeneratePage;
